import { t } from 'i18next';
import { Camera } from 'three';
import _ from 'underscore';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { DesignFeature } from '../../../../../../../react/components/skyview/right-menu/tools/design-tool-menu/sections/design-feature';
import { isDefined } from '../../../../../../../utils/variables';
import cameraManager from '../../../../../../camera-manager';
import {
  DesignNode,
  RevertChange,
} from '../../../../../../elements/design/design-node/design-node';
import {
  getLineSegmentFaces,
  LineSegmentId,
  LineSegmentNode,
} from '../../../../../../elements/design/design-node/utils/line-segment-node';
import { PointId } from '../../../../../../elements/design/design-node/utils/point-node';
import { AttributeData, AttributeId } from '../../../../../../elements/design/skymap-extras';
import { publish, SubscriptionTopic } from '../../../../../../messaging/pubsub';
import {
  isPolygonClockwise,
  polygonContainsPoint2d,
} from '../../../../../../utilities/math-helpers';
import { getWebWorkerManager } from '../../../../../../workers/web-worker-manager';
import { DesignTool } from '../../../../../design-tool';
import { addAndCommitAction } from '../../utils/actions';
import { densifyCurve } from '../../utils/curve';
import { listLinesAndPolygonsBySelection } from '../../utils/multi-select';
import { deselect, getPointIdArray, SelectionResult } from '../../utils/select';

const activateTriangualateFeature = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.functions.items.triangulateSelection', { ns: 'skyviewDesign' }),
    action: () => {
      tool.activeFeature = DesignFeature.Triangulate;
      publish(SubscriptionTopic.DesignToolMenuStateChanged, {
        designMode: false,
        snapMode: false,
        placeMode: false,
        features: true,
      });

      tool.autoTriggerTriangulation = true;
      publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);

      tool.contextMenuCallback?.();
    },
    hotkey: 'T',
  };
};

function getPointIdsFromLineSegmentIds(designNode: DesignNode, lineSegmentIds: LineSegmentId[]) {
  return getPointIdArray(lineSegmentIds.map((x) => designNode.getLineSegmentById(x)));
}

const getSelectedGeometry = (tool: DesignTool, selection: SelectionResult) => {
  const selectedLines = [...selection.lineSegments];
  const selectedPoints = [...selection.points];

  const result = listLinesAndPolygonsBySelection(
    tool.asset.designNode,
    selectedLines.filter((x) => {
      return getLineSegmentFaces(tool.asset.designNode, x).length < 2;
    }),
  );

  const pointIdsConnectedToLineSegments = getPointIdsFromLineSegmentIds(
    tool.asset.designNode,
    selectedLines,
  );

  return {
    lines: result.filter((x) => !x.isPolygon).map((x) => x.lines),
    polygons: result.filter((x) => x.isPolygon).map((x) => x.lines),
    points: selectedPoints.filter((x) => !pointIdsConnectedToLineSegments.includes(x)),
  };
};

const getTriangulationResult = async (
  tool: DesignTool,
  points: PointId[],
  lines: LineSegmentNode[][],
  polygons: LineSegmentNode[][],
) => {
  const camera = cameraManager().getActiveCamera()!.getTHREECamera().clone();

  /**
   * Special case when multiple lines are selected, but no points or polygons.
   *
   * Then we want to triangulate each line pair separately.
   * This way we avoid getting triangles outside of the outer lines that are not
   * possible to clean up.
   *
   * Note: this strategy works well for lines that follow the same general path, but does
   * not work for lines with branching paths.
   * (in those cases the user would have to define an outer polygon)
   */
  if (lines.length > 1 && polygons.length === 0 && points.length === 0) {
    return await triangulateSortedPairs(tool, lines, camera);
  } else {
    const usedPointIds = _.uniq([
      ...getPointIdsFromLineSegmentIds(tool.asset.designNode, tool.currentSelection.lineSegments),
      ...tool.currentSelection.points,
    ]);
    const flatPolygons = polygons.map((x) => x.map((x) => x.values));
    const edges = [...flatPolygons.flat(), ...lines.flatMap((x) => x.map((x) => x.values))];

    return await triangulatePointsCdt2d(tool, usedPointIds, edges, flatPolygons, points, camera);
  }
};

type DensifiedCurves = {
  /** The changes from replacing the curves. */
  changes: RevertChange[];
  /** Replaces the curves again, after the changes have been reverted. */
  redo: () => RevertChange[];
  /** Reverts the changes and restores the selection, if the triangulation is not applied. */
  revert: () => void;
};

/**
 * Triangles can not follow curves, so the selected curves are replaced with points along the curves,
 * connected by straight line segments, before triangulating. The selection is updated with the new
 * line segments. Returns undefined if no curves are selected.
 *
 * The changes are not undoable on their own, they are passed on to applyTriangulationResult.
 */
const densifySelectedCurves = (tool: DesignTool): DensifiedCurves | undefined => {
  const node = tool.asset.designNode;
  const selection = tool.currentSelection.lineSegments;
  const curveIds = selection.filter(
    (id) =>
      isDefined(node.getLineSegmentById(id).curve) && getLineSegmentFaces(node, id).length === 0,
  );

  if (curveIds.length === 0) {
    return undefined;
  }

  const densify = () => {
    const changes: RevertChange[] = [];
    const replaced = new Map<LineSegmentId, LineSegmentId[]>();

    for (const id of curveIds) {
      const result = densifyCurve(node, id);
      changes.unshift(...result.changes);
      replaced.set(id, result.lineSegmentIds);
    }

    return { changes, replaced };
  };

  const { changes, replaced } = densify();

  tool.currentSelection.lineSegments = selection.flatMap((id) => replaced.get(id) ?? [id]);
  tool.refreshSelectedLineSegmentsCursor([]);
  node.refresh();

  return {
    changes,
    redo: () => densify().changes,
    revert: () => {
      node.revert(changes);
      node.refresh();
      tool.currentSelection.lineSegments = selection;
      tool.refreshSelectedLineSegmentsCursor([]);
    },
  };
};

/**
 * @param densifiedCurves The curves that were replaced before triangulating, undone together with the triangulation.
 */
const applyTriangulationResult = (
  tool: DesignTool,
  indices: PointId[],
  densifiedCurves?: DensifiedCurves,
): ContextMenuItem => {
  return {
    title: t('contextMenu.applyTriangulation', { ns: 'skyviewDesign' }),
    action: () => {
      const activeAttributes = tool.getActiveAttributes();
      let changes: RevertChange[] = [];
      // The curves are already replaced when the triangulation is applied the first time.
      let densifyChanges = densifiedCurves?.changes ?? [];
      let curvesReplaced = true;

      addAndCommitAction(tool, {
        commit: () => {
          if (!curvesReplaced && densifiedCurves) {
            densifyChanges = densifiedCurves.redo();
          }

          curvesReplaced = true;
          changes = [...addFacesToGeometry(tool, indices, activeAttributes), ...densifyChanges];
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
        rollback: () => {
          tool.asset.designNode.revert(changes);
          curvesReplaced = false;
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
      });
      deselect(tool);
      tool.contextMenuCallback?.();
    },
  };
};

/**
 * For each triangle, checks if the center point of the triangle is contained within any polygon.
 * If the triangle is not contianed within any polygon it should be removed from the result.
 *
 * If the triangle contains any protected points it will not be removed.
 */
const cutTrianglesToBoundary = (
  tool: DesignTool,
  triangles: PointId[],
  polygons: [PointId, PointId][][],
  protectedPoints: PointId[],
  axis1: 'x' | 'y' | 'z',
  axis2: 'x' | 'y' | 'z',
) => {
  const result: PointId[] = [];

  /**
   * Uses axis 1 and axis 2 as main axises and disregards the third axis.
   */
  const getVertexByAxis = (index: PointId) => {
    const v = tool.asset.designNode.getVertexByPointId(index, 'world');
    const [x, y] = [v[axis1], v[axis2]];
    return v.set(x, y, 0);
  };

  // Get polygon vertices.
  const polygonVertices = polygons.map((pol) =>
    _.uniq(pol.flat()).map((idx) => getVertexByAxis(idx)),
  );

  // If we dont have any polygons we will just triangulate everything.
  if (polygons.length === 0) {
    return triangles;
  }

  for (let i = 0; i < triangles.length; i += 3) {
    const [a, b, c] = [triangles[i], triangles[i + 1], triangles[i + 2]];

    // Dont remove any triangles containing protected points.
    if (protectedPoints.includes(a) || protectedPoints.includes(b) || protectedPoints.includes(c)) {
      result.push(a, b, c);
      continue;
    }

    const va = getVertexByAxis(a);
    const vb = getVertexByAxis(b);
    const vc = getVertexByAxis(c);

    // Get center point of the triangle.
    const center = va.add(vb).add(vc).divideScalar(3);

    if (polygonVertices.some((x) => polygonContainsPoint2d(center, x))) {
      result.push(a, b, c);
    }
  }

  return result;
};

/**
 * @param pointIndices an array of point indices.
 * @param edges an array with index pairs for each edge.
 * example: [[0,1],[1,2],[2,3],[3,0]]
 * @returns
 */
const triangulatePointsCdt2d = async (
  tool: DesignTool,
  pointIndices: PointId[],
  edges: [PointId, PointId][],
  polygons: [PointId, PointId][][],
  protectedPoints: PointId[],
  camera: Camera,
): Promise<PointId[]> => {
  /**
   * Get the corresponding local index for each edge.
   * i.e. the position of that index in the pointIndices array.
   */
  const edgeArrayIndices: [number, number][] = [];
  for (const edge of edges) {
    edgeArrayIndices.push([pointIndices.indexOf(edge[0]), pointIndices.indexOf(edge[1])]);
  }

  let triangles: PointId[] = [];

  triangles = await cdt2dTriangulation(
    tool,
    pointIndices,
    edgeArrayIndices,
    polygons,
    protectedPoints,
    'x',
    'y',
  );

  // Attempt to triangulate vertically if the result is empty.
  if (triangles.length === 0) {
    // XZ plane first.
    triangles = await cdt2dTriangulation(
      tool,
      pointIndices,
      edgeArrayIndices,
      polygons,
      protectedPoints,
      'x',
      'z',
    );

    // If XZ result is also empty attempt to triangulate in ZY plane.
    if (triangles.length === 0) {
      triangles = await cdt2dTriangulation(
        tool,
        pointIndices,
        edgeArrayIndices,
        polygons,
        protectedPoints,
        'z',
        'y',
      );
    }

    /**
     * Check if the first triangle is clockwise in relation to the camera.
     * If it is we want to reverse the triangle order so that the triangles face the camera.
     *
     * We only do this check for vertical triangulations for now.
     */
    if (triangles.length > 2) {
      const getPointScreenPosition = (index: PointId) => {
        const projectedPoint = tool.asset.designNode
          .getVertexByPointId(index, 'world')
          .project(camera);
        return projectedPoint.setY(-projectedPoint.y);
      };

      const p1 = getPointScreenPosition(triangles[0]);
      const p2 = getPointScreenPosition(triangles[1]);
      const p3 = getPointScreenPosition(triangles[2]);
      if (isPolygonClockwise([p1, p2, p3])) {
        triangles.reverse();
      }
    }
  }

  return triangles;
};

/**
 * @param xyPoints Points in xy coordinates
 * example: [ [x,y], [x2,y2], [x3,y3] ] etc.
 * @param xyEdges Edges indices (does not need to be connected)
 * example: [ [idx1, idx2], [idx2, idx3], [idx3, idx4] ] etc...
 * @returns
 */
const cdt2dTriangulation = async (
  tool: DesignTool,
  pointIndices: PointId[],
  edgeArrayIndices: [number, number][],
  polygons: [PointId, PointId][][],
  protectedPoints: PointId[],
  axis1: 'x' | 'y' | 'z',
  axis2: 'x' | 'y' | 'z',
): Promise<PointId[]> => {
  const points2d = pointIndices.map((p) => {
    const vertex = tool.asset.designNode.getVertexByPointId(p, 'world');
    return [vertex[axis1], vertex[axis2]];
  });

  const result = await getWebWorkerManager().spawnTriangulationWorker(points2d, edgeArrayIndices);

  const triangles = result
    .flatMap((x: number | number[]) => (Array.isArray(x) ? x : [x]))
    .map((x: number) => pointIndices[x]);

  return cutTrianglesToBoundary(tool, triangles, polygons, protectedPoints, axis1, axis2);
};

/**
 * Returns an array with pairs of lines.
 * The pairs will be determined by their position in relation to eachother,
 * so only "neighbouring lines" will become pairs.
 *
 * The direction of the line will also be adjusted so they are both in the same direction.
 */
const triangulateSortedPairs = async (
  tool: DesignTool,
  items: LineSegmentNode[][],
  camera: Camera,
): Promise<PointId[]> => {
  const pointIdGroups = items.map((x) => getPointIdArray(x));

  const linePairs: {
    indexA: number;
    indexB: number;
    pairId: string;
    distance: number;
    lineA: PointId[];
    lineB: PointId[];
  }[] = [];

  /**
   * Creates a unique pair id (always places smallest id first).
   */
  const getPairId = (a: number, b: number) =>
    `${(a < b ? a : b).toString().padStart(4, '0')}${(a > b ? a : b).toString().padStart(4, '0')}`;

  for (let a = 0; a < pointIdGroups.length; a++) {
    for (let b = 0; b < pointIdGroups.length; b++) {
      if (a === b) {
        continue;
      }

      const lineA = pointIdGroups[a];
      const lineB = pointIdGroups[b];

      const startA = tool.asset.designNode.getVertexByPointId(lineA[0], 'world').setZ(0);
      const startB = tool.asset.designNode.getVertexByPointId(lineB[0], 'world').setZ(0);
      const endB = tool.asset.designNode
        .getVertexByPointId(lineB[lineB.length - 1], 'world')
        .setZ(0);

      const distance = startA.distanceTo(startB);
      const distanceReversed = startA.distanceTo(endB);

      linePairs.push({
        pairId: getPairId(a, b),
        indexA: a,
        indexB: b,
        distance: Math.min(distanceReversed, distance),
        lineA: lineA,
        lineB: distanceReversed < distance ? [...lineB].reverse() : lineB,
      });
    }
  }

  // Sort the pairs by distance to find out pair is furthest away from eachother.
  linePairs.sort((a, b) => (a.distance > b.distance ? 1 : -1));

  // First and last line id is included in the line that is furthest away (last index).
  const firstIndex = linePairs[linePairs.length - 1].indexA;
  const lastIndex = linePairs[linePairs.length - 1].indexB;

  // Sandwhich the indices, then include the id of each pair to get each id in order of distance.
  const lineIndices: number[] = _.uniq([
    firstIndex,
    ...linePairs
      .filter((x) => x.indexA === firstIndex || x.indexB === firstIndex)
      .flatMap((x) => [x.indexA, x.indexB]),
    lastIndex,
  ]);

  /**
   * Build the pair IDs.
   * For example:
   * [2,3,0,1] -> ['00020003', '00000003', '00000001']
   */
  const pairIdsToInclude: string[] = [];
  for (let i = 0; i < lineIndices.length - 1; i++) {
    pairIdsToInclude.push(getPairId(lineIndices[i], lineIndices[i + 1]));
  }

  const uniquePairs = pairIdsToInclude.map((pairId) => linePairs.find((x) => x.pairId === pairId)!);

  const triangleIndices: PointId[] = [];

  for (const pair of uniquePairs) {
    const indicesBReversed = [...pair.lineB].reverse();
    const polygon = [...pair.lineA, ...indicesBReversed] as PointId[];

    const edges: [PointId, PointId][] = polygon.map((x, idx) => [
      x,
      polygon[(idx + 1) % polygon.length],
    ]);

    const result = await triangulatePointsCdt2d(tool, polygon, edges, [edges], [], camera);

    triangleIndices.push(...result);
  }

  return triangleIndices;
};

/**
 *
 * @param tool
 * @param existingLines Pass existing lines as a reference to avoid creating duplicates.
 * @param indices Triangle indices (should always be divisible by 3).
 */
const addFacesToGeometry = (
  tool: DesignTool,
  indices: PointId[],
  activeAttributes?: Map<AttributeId, AttributeData>,
) => {
  const newFaces: [PointId, PointId, PointId][] = [];
  const linesToCheck: [PointId, PointId][] = [];

  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i];
    const b = indices[i + 1];
    const c = indices[i + 2];

    newFaces.push([a, b, c]);
    linesToCheck.push([a, b]);
    linesToCheck.push([a, c]);
    linesToCheck.push([b, c]);
  }

  const changes: RevertChange[] = [];

  for (const values of linesToCheck) {
    if (!isDefined(tool.asset.designNode.findLineSegmentByPointIds(values[0], values[1]))) {
      changes.unshift(tool.asset.designNode.addLineSegment(values[0], values[1], activeAttributes));
    }
  }

  for (const face of newFaces) {
    if (!isDefined(tool.asset.designNode.findFaceByPointIds(...face))) {
      changes.unshift(tool.asset.designNode.addFace(...face, activeAttributes));
    }
  }

  return changes;
};

export const triangulateFunctions = {
  activateTriangualateFeature,
  densifySelectedCurves,
  getSelectedGeometry,
  getTriangulationResult,
  applyTriangulationResult,
};
